import styled from '@emotion/styled'

import {
  Alert,
  AssetTableTransfer,
  Break,
  DurationButtons,
  EditableAssetTable,
  FieldSet,
  RadioWithLabel,
  Section,
  SelfSelectPortfolioDetails,
  Space,
  StrategyDisplay,
  Typography,
  ValidationMessage
} from '../../../../../components/core-v2'
import Icon from '../../../../../components/core-v2/atoms/Icon'
import IconButton from '../../../../../components/core-v2/molecules/iconButton'
import LinkIcon from '../../../../../components/core-v2/molecules/linkIcon'
import { useAppSelector } from '../../../store/hooks'
import { round } from '../../../util'
import { getMergedDisallowedStrategies } from '../../00-builder/state/mappers'
import { FundingSource } from '../../02-terms/state/terms-slice'
import { ChooseAssetModal } from '../choose-assets'
import { ChoosePortfolioModal } from '../choose-portfolio'
import {
  PortfolioStepActions,
  SearchAssetState,
  SearchPortfolioState,
  SelfSelectPortfolioState
} from '../state/portfolio-slice'

type SelfSelectEditorProps = {
  actions: PortfolioStepActions
  searchAssetState: SearchAssetState
  searchPortfolioState: SearchPortfolioState
  state: SelfSelectPortfolioState
}

const StyledValidation = styled.div`
  * {
    text-align: right;
  }
`

const AssetFinderButton = ({ onClick }: { onClick: () => void }) => (
  <Section pb={6} type="primary">
    <Space mx={5}>
      <IconButton
        disabled={false}
        icon={<Icon name="circleAdd" size="default" />}
        iconPosition="left"
        operationType="progress"
        text="Add an asset"
        width="100%"
        onClick={onClick}
      />
    </Space>
  </Section>
)

const PortfolioFinderButton = ({ onClick }: { onClick: () => void }) => (
  <Section pb={6} type="primary">
    <Space mx={5}>
      <IconButton
        disabled={false}
        icon={<Icon name="search" size="default" />}
        iconPosition="left"
        operationType="progress"
        text="Search for a portfolio"
        width="100%"
        onClick={onClick}
      />
    </Space>
  </Section>
)

export const ActiveEditor = ({
  actions,
  state
}: Pick<SelfSelectEditorProps, 'actions' | 'state'>) => {
  const { assets } = state
  const strategies = state.availableStrategies
  const builderState = useAppSelector(state => state.builder)
  const businessUnitDisallowedStrategies = builderState.businessUnit?.disallowedStrategies
  const portfolioState = useAppSelector(state => state.portfolio)
  const { isTransfer: isTransferInSavedState } = portfolioState
  const termsState = useAppSelector(state => state.terms)
  const { fundingSource } = termsState.terms
  const isTransfer =
    fundingSource === FundingSource.Unknown
      ? isTransferInSavedState
      : fundingSource === FundingSource.Transfer

  return (
    <Section pb={6} type="primary">
      {state.selfSelectType === 'modify' && (
        <Space mb={6} mx={5}>
          <LinkIcon
            disabled={false}
            icon={<Icon name="search" size="default" />}
            iconPosition="left"
            text="Choose another model portfolio to modify"
            onClick={() => actions.chooseModelPortfolio()}
          />
        </Space>
      )}
      <Space mb={6} mx={5}>
        <Space pb={5}>
          <Break />
        </Space>
        <Space mb={5}>
          <DurationButtons
            items={['6m', '1y', '3y', '5y', '10y']}
            selectedItem={state.selectedAssetDuration}
            onSelect={item => actions.setSelectedAssetDuration(item)}
          />
        </Space>
        <Break />
        {isTransfer && (
          <Space mt={5}>
            <AssetTableTransfer
              assets={assets}
              onChangeAssetUnits={(assetId, units) =>
                actions.changeSelfSelectTransferUnits({ assetId, units })
              }
              onClickAsset={assetId => actions.inspectAsset(assetId)}
              onRemoveAsset={assetId =>
                actions.removeAssetFromSelfSelect({
                  id: assetId,
                  businessUnitDisallowedStrategies,
                  isTransfer
                })
              }
            />
          </Space>
        )}
        {!isTransfer && (
          <>
            <Space mt={5}>
              <EditableAssetTable
                assets={assets}
                onChangeAssetWeight={(assetId, weight) =>
                  actions.changeSelfSelectAssetWeight({ assetId, weight })
                }
                onClickAsset={assetId => actions.inspectAsset(assetId)}
                onRemoveAsset={assetId =>
                  actions.removeAssetFromSelfSelect({
                    id: assetId,
                    businessUnitDisallowedStrategies,
                    isTransfer
                  })
                }
              />
            </Space>
            <Space isflex alignItems="center" justifyContent="space-between" my={5}>
              <LinkIcon
                disabled={false}
                icon={<Icon name="equalise" size="default" />}
                iconPosition="left"
                text="Equalise weights"
                onClick={() => actions.equaliseSelfSelectWeights()}
              />
              <LinkIcon
                disabled={false}
                icon={<Icon name="scales" size="default" />}
                iconPosition="left"
                text="Scale weights to 100%"
                onClick={() => actions.scaleSelfSelectWeights()}
              />
              <Typography fontSize="15px" fontWeight="800" letterSpacing="0" variant="b3">
                Total: {round(state.totalWeights * 100, 2)}%
              </Typography>
            </Space>
          </>
        )}
      </Space>

      <Space mb={5} mx={5}>
        <StyledValidation>
          <ValidationMessage errors={state.errors} fieldName="total-weights" />
        </StyledValidation>
      </Space>

      <AssetFinderButton onClick={() => actions.chooseAsset()} />

      {state.isTaxClassificationWarning && !state.isInvalidTaxClassification && (
        <Space mb={6} mt={0} mx={5}>
          <Alert title="This portfolio's tax classification does not match the note" type="error">
            You may wish to change the portfolio to maintain a consistent tax clasification
          </Alert>
        </Space>
      )}

      {state.isInvalidTaxClassification && (
        <Space mb={6} mt={0} mx={5}>
          <Alert title="This portfolio's tax classification is invalid" type="error">
            You must change the portfolio in order to proceed
          </Alert>
        </Space>
      )}

      <Section mx={5} pt={6} px={5} type="secondary">
        <SelfSelectPortfolioDetails
          calculatedMultiplier={state.calculatedMultiplier}
          portfolio={state.portfolio}
          taxClassification={state.taxClassification}
          onChangeRebalancingFreq={freq => actions.setSelfSelectRebalancingFreq(freq)}
          onChangeRebalancingTolerance={tol => actions.setSelfSelectRebalancingTolerance(tol)}
          onChangeRebalancingType={type => actions.setSelfSelectRebalancingType(type)}
        />
        <Space isflex alignItems="center" flexDirection="column" pb={6}>
          <Space my={6}>
            <Typography fontSize="13px" fontWeight="800" variant="b3">
              Available strategies (choose in the next step)
            </Typography>
          </Space>
          <StrategyDisplay strategies={strategies} />
        </Space>
      </Section>
    </Section>
  )
}

export const SelfSelectPortfolioEditor = ({
  actions,
  searchAssetState,
  searchPortfolioState,
  state
}: SelfSelectEditorProps) => {
  const builderState = useAppSelector(state => state.builder)
  const businessUnitDisallowedStrategies = builderState.businessUnit?.disallowedStrategies
  const portfolioState = useAppSelector(state => state.portfolio)
  const { isTransfer: isTransferInSavedState } = portfolioState
  const termsState = useAppSelector(state => state.terms)
  const { fundingSource } = termsState.terms
  const isTransfer =
    fundingSource === FundingSource.Unknown
      ? isTransferInSavedState
      : fundingSource === FundingSource.Transfer
  if (isTransfer && !state.selfSelectType) {
    actions.setSelfSelectType('new')
  }

  return (
    <>
      {!isTransfer && (
        <Section px={5} py={6} type="primary">
          <FieldSet direction="horizontal">
            <RadioWithLabel
              checked={state.selfSelectType === 'new'}
              onChange={() => actions.setSelfSelectType('new')}
            >
              Build a new self select
            </RadioWithLabel>
            <RadioWithLabel
              checked={state.selfSelectType === 'modify'}
              onChange={() => actions.setSelfSelectType('modify')}
            >
              Modify a portfolio
            </RadioWithLabel>
          </FieldSet>
        </Section>
      )}
      {!state.isChosenAsset && state.selfSelectType === 'new' && (
        <AssetFinderButton onClick={() => actions.chooseAsset()} />
      )}
      {!state.isChosenAsset && state.selfSelectType === 'modify' && (
        <PortfolioFinderButton onClick={() => actions.chooseModelPortfolio()} />
      )}
      {state.isChoosingAsset && (
        <ChooseAssetModal
          state={searchAssetState}
          onChangeSearchTerm={term => actions.changeAssetSearchTerm(term)}
          onClickBackToSearch={() => actions.goBackToAssetSearch()}
          onInspectAsset={id => actions.inspectSelfSelectAsset(id)}
          onSelectAsset={asset =>
            actions.setAssetInSelfSelect({
              asset,
              businessUnitDisallowedStrategies,
              isTransfer
            })
          }
          onStopChoosingAsset={() => actions.stopChoosingAsset()}
        />
      )}
      {state.isChoosingBasePortfolio && (
        <ChoosePortfolioModal
          state={searchPortfolioState}
          onChangeSearchTerm={term => actions.changeSearchTerm(term)}
          onClickBackToSearch={() => actions.goBackToSearch()}
          onInspectPortfolio={id => actions.inspectPortfolio(id)}
          onSelectPortfolio={portfolio =>
            actions.setBasePortfolio({
              model: portfolio as any,
              disallowedStrategiesFromState: getMergedDisallowedStrategies(
                businessUnitDisallowedStrategies,
                portfolio.disallowedStrategies
              )
            })
          }
          onStopChoosingPortfolio={() => actions.stopChoosingPortfolio()}
        />
      )}

      {state.isChosenAsset && <ActiveEditor actions={actions} state={state} />}
    </>
  )
}
